import type { ReactNode } from 'react'
import { Repeat2, PenLine, Layers } from 'lucide-react'

/* ==================================================================
 * RemakeTag — marks a campaign as a Viral Video Remake job
 * ==================================================================
 * One pill, used everywhere the brand side needs to say "this campaign
 * is a remake job": the Brief step, the launch review, and the campaign
 * list. Same "electric blue" gradient as the creator app and bmc-admin
 * badges so the hierarchy looks like one thing across all three surfaces.
 *
 * Blue is deliberately NOT the pink brand accent, and not amber (amber
 * reads as "warning"/"needs attention" elsewhere in this app — see e.g.
 * the deadline/waitlist chips in HiredCreators.tsx, InvitedCreators.tsx):
 * this labels what kind of job a campaign is, a neutral fact, not
 * something to worry about. Solid gradient + white text (not a translucent
 * tint) so it reads as its own thing next to the status badge, not another
 * muted status pill.
 * ================================================================== */

// Exported so every remake-aware view (Step2/Step3 of CampaignNew, CampaignView,
// CreatorAppPreview) draws from these two values instead of re-declaring the
// hex codes locally — one place to change the accent, no risk of one surface
// drifting a shade off from the others.
export const REMAKE_GRADIENT = 'linear-gradient(135deg, #38BDF8, #3B82F6)' // sky-400 → blue-500
export const REMAKE_BLUE = '#38BDF8'

export function RemakeTag({ compact = false }: {
  /** Smaller size for tight rows like the campaign list — the label is
   *  always the full "Viral Video Remake" either way. An abbreviated
   *  "Remake"-only form shipped first but was dropped 2026-09-02 per
   *  product feedback: the tag is the only signal a brand gets while
   *  scanning the campaign list, so it has to name the hierarchy on its own. */
  compact?: boolean
}) {
  return (
    <span
      title="Viral Video Remake — creators recreate specific videos with your product"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: compact ? 10 : 11,
        fontWeight: 700,
        padding: compact ? '2px 8px' : '3px 10px',
        borderRadius: 999,
        background: REMAKE_GRADIENT,
        color: '#fff',
        boxShadow: '0 1px 4px rgba(59, 130, 246, 0.35)',
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      <Repeat2 size={compact ? 10 : 12} strokeWidth={2.5} />
      Viral Video Remake
    </span>
  )
}

/* ==================================================================
 * MultiProductTag — marks a campaign as covering more than one product
 * ==================================================================
 * Same pattern as RemakeTag, deliberately its own hue: indigo, not the pink
 * brand accent, not amber ("warning" elsewhere), and not the sky→blue used
 * for Viral Video Remake above. A first cyan version shipped, then was
 * dropped the same day — cyan and sky-blue read as near-identical at badge
 * size, and Remake + Multi-Product are the one pair that can genuinely
 * appear side by side on the same campaign, so they're the pair that most
 * needs to be tell-apart-able. Indigo is clearly more violet/purple than
 * either blue. Shared verbatim (same gradient) with the creator app and
 * admin badges.
 * ================================================================== */

// Exported so every multi-product-aware view (CampaignNew's Step1/preview,
// Campaigns list, CampaignView) draws from these values instead of
// re-declaring the hex codes locally.
export const MULTI_PRODUCT_GRADIENT = 'linear-gradient(135deg, #818CF8, #4F46E5)' // indigo-400 → indigo-600
export const MULTI_PRODUCT_INDIGO = '#818CF8'
/** Exact label — the creator app and admin use this same string. */
export const MULTI_PRODUCT_LABEL = 'Product-Bundle'

export function MultiProductTag({ compact = false, count }: {
  /** Smaller size for tight rows like the campaign list. */
  compact?: boolean
  /** When provided, the label becomes "N Products" instead of "Multi-Product" —
   *  used where there's room and the exact count is useful (e.g. the campaign
   *  detail header). Omit it for tight rows, same as RemakeTag's full label. */
  count?: number
}) {
  return (
    <span
      title={count ? `${count} products — creators split their required videos across them however they like, no per-product limit` : 'Product bundle campaign'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: compact ? 10 : 11,
        fontWeight: 700,
        padding: compact ? '2px 8px' : '3px 10px',
        borderRadius: 999,
        background: MULTI_PRODUCT_GRADIENT,
        color: '#fff',
        boxShadow: '0 1px 4px rgba(79, 70, 229, 0.35)',
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      <Layers size={compact ? 10 : 12} strokeWidth={2.5} />
      {count ? `${count} Products` : MULTI_PRODUCT_LABEL}
    </span>
  )
}

/* ==================================================================
 * TiktokCollabTag — the brand's own TikTok Shop collaboration type
 * ==================================================================
 * Optional, brand-entered in Step 2 of the wizard (2026-09-25) — mirrors
 * TIKTOK_COLLAB_TYPES in CampaignNew.steps.tsx and
 * cr_campaigns.tiktok_collab_type. Duplicated here rather than imported: this
 * file is imported BY CampaignNew.steps.tsx, so the reverse import would be
 * circular. Keep the three labels in sync if the wording changes anywhere.
 *
 * Deliberately NOT the solid gradient RemakeTag/MultiProductTag use above —
 * those two mark a job HIERARCHY this platform builds the brief and matching
 * around differently; this is the brand's own classification of an external
 * TikTok Shop program, closer to a fact label than a feature flag, so it
 * reads at a lighter weight. Renders nothing when unset — "not specified" is
 * this field's normal resting state, not a gap to flag on a campaign the
 * brand simply didn't have an answer for.
 * ================================================================== */

const TIKTOK_COLLAB_TAG_META: Record<string, { label: string; bg: string; fg: string }> = {
  open_collaboration: { label: 'Open Collaboration', bg: 'rgba(20, 184, 166, 0.12)', fg: '#0d9488' },   // teal
  target_collaboration: { label: 'Target Collaboration', bg: 'rgba(139, 92, 246, 0.12)', fg: '#7c3aed' }, // violet
  tap: { label: 'TAP', bg: 'rgba(6, 182, 212, 0.12)', fg: '#0891b2' },                                    // cyan
}

export function TiktokCollabTag({ value, compact = false }: {
  value?: string | null
  compact?: boolean
}) {
  const meta = value ? TIKTOK_COLLAB_TAG_META[value] : undefined
  if (!meta) return null
  return (
    <span
      title="TikTok Shop collaboration type — the brand's own classification, not something this platform runs differently"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: compact ? 10 : 11,
        fontWeight: 700,
        padding: compact ? '2px 8px' : '3px 10px',
        borderRadius: 999,
        background: meta.bg,
        color: meta.fg,
        border: `1px solid ${meta.fg}33`,
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {meta.label}
    </span>
  )
}

/* ==================================================================
 * RemakeVideoCard — one video in a remake worklist
 * ==================================================================
 * A remake brief's videos aren't inspiration a brand skims once — they're a
 * numbered worklist a creator has to work through one at a time, each with
 * its own instruction. Rendered as flowing text (name, then link, then a
 * paragraph of description, repeated per video with no boundary between
 * them) they blur into one wall of copy the moment any description runs
 * long — exactly what happened on both the Step 3 review and the read-only
 * CampaignView page. This gives every video its own bordered card with a
 * numbered chip, so "video 2 ends, video 3 begins" is a visual fact, not
 * something the reader has to parse from paragraph breaks.
 *
 * Shared by CampaignNew's Step3 review and CampaignView (the post-launch
 * read-only page) — the two places brand-side remake briefs get long enough
 * to need this. Standard (non-remake) briefs keep their own flatter list
 * rendering in each of those files; this component is remake-only.
 * ================================================================== */
export function RemakeVideoCard({ index, name, url, description, urlSlot }: {
  index: number
  name: string
  url: string
  description?: string | null
  /** Optional custom rendering for the URL row (e.g. CampaignView's read-only
   *  input + open-in-new-tab button) — falls back to a plain link. */
  urlSlot?: ReactNode
}) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 8,
      padding: 12, borderRadius: 10,
      border: '1px solid var(--border-primary)',
      background: 'var(--bg-secondary)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span style={{
          flexShrink: 0, width: 20, height: 20, marginTop: 1, borderRadius: '50%',
          background: REMAKE_GRADIENT, color: '#fff', fontSize: 11, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {index + 1}
        </span>
        {/* overflowWrap so a single long unbroken token (a name typed with no
            spaces) wraps inside the card instead of forcing it wider than the
            column — plain wordBreak still lets very long tokens overflow. */}
        <span style={{
          fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
          lineHeight: 1.4, overflowWrap: 'anywhere', paddingTop: 1,
        }}>
          {name}
        </span>
      </div>
      <div style={{ paddingLeft: 28 }}>
        {urlSlot ?? (
          <a href={url} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 12, color: '#FB3AAE', textDecoration: 'none', wordBreak: 'break-all' }}>
            {url}
          </a>
        )}
      </div>
      {/* A left accent stripe read as an alert/warning callout, which this
          isn't — it's an instruction. Soft all-around border + an icon next
          to the label carries the same "this is set apart" cue without that
          connotation. */}
      {description && (
        <div style={{
          marginLeft: 28, padding: '9px 11px', borderRadius: 8,
          background: 'rgba(56, 189, 248, 0.05)',
          border: '1px solid rgba(56, 189, 248, 0.20)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
            <PenLine size={11} color={REMAKE_BLUE} strokeWidth={2.25} />
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
              textTransform: 'uppercase', color: REMAKE_BLUE,
            }}>
              What to remake
            </span>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {description}
          </div>
        </div>
      )}
    </div>
  )
}
